import helpers from '../data/cclone/helpers'
import { webRequestClient } from '@/services'
import { get } from 'lodash'
import { errorHandler } from '@/utils/helpers'
import {
    toStartISO,
    toEndISO,
    toStartISOFormatted,
    toEndISOFormatted,
    mapCollectOnline,
    mapCollectOffline,
    mapTaxonomiesBreakdown,
    isLessThan3Days
} from './helpers'

const actions = {
    retrieveCcloneList({ commit, rootState }) {
        const _route = helpers.buildCclonesRoute(rootState.account)
        commit('setLoading', true)
        return webRequestClient
            .get(_route.route, _route.params)
            .then(cclones => {
                commit('setLoading', true)
                if (get(cclones, 'status') === 'KO') {
                    errorHandler('Error while retrieving cclones', commit)
                    commit('setLoading', false)
                } else {
                    commit('setCcloneList', helpers.parseCclonesData(cclones.list))
                    commit('setLoading', false)
                }
            })
            .catch(error => {
                commit('setLoading', false)
                errorHandler(error, commit)
            })
    },
    retrieveExpiringAudiences({ commit, rootState}) {
        commit('setExpiringAudiencesLoading', true)
        const account_id = rootState.account.accountId
        return webRequestClient
            .get('/audience_admin/audiences/expiring/count/json', { params: { account_id } })
            .then((count) => {
                commit('setExpiringAudiences', count.number_audiences)
                commit('setExpiringAudiencesLoading', false)
            })
            .catch(error => {
                commit('setExpiringAudiencesLoading', false)
                commit('setExpiringAudiencesError', true)
                errorHandler(error, commit)
            })
    },
    retrieveExpiringTransfers({ commit, rootState}) {
        commit('setExpiringTransfersLoading', true)
        const account_id = rootState.account.accountId
        return webRequestClient
            .get('/audience_admin/data_transfers/expiring/count/json', { params: { account_id } })
            .then((count) => {
                commit('setExpiringTransfers', count.number_of_transfers)
                commit('setExpiringTransfersLoading', false)
            })
            .catch(error => {
                commit('setExpiringTransfersLoading', false)
                commit('setExpiringTransfersError', true)
                errorHandler(error, commit)
            })
    },
    async retrieveBreakdown({ commit, dispatch, rootState }, requestTry = 0) {
        try {
            commit('setBreakdownLoading', true)
            const res = await webRequestClient.post('/audience_admin/criteria/statistic.json', {
                account_id: rootState.account.accountId,
                method: 'breakdown',
                version: 1,
                formula: ''
            })

            if(res.status === 'NOT_READY') {
                if(requestTry < 30) {
                    setTimeout(() => dispatch('retrieveBreakdown', requestTry+= 1), 1000)
                }
            }
            else {
                commit('setBreakdown', res)
                commit('setPercentBreakdown', res.clusters)
                commit('setAffinityBreakdown', res.clusters)
                commit('setBreakdownLoading', false)
            }
        }
        catch (error) {
            commit('setBreakdownLoading', false)
            const errorAuthorization = [
                '"breakdown.v1: Access forbidden: this account isn\'t authorized to access to Breakdown"',
                '"breakdown.v1: Access forbidden: this account isn\'t authorized to access to Breakdown Bigsea Sociodemo and Bigsea Behavioral"'
            ]
            if(!errorAuthorization.includes(error.apiError?.message)) {
                errorHandler(error.apiError?.message ? error.apiError.message : 'Error while retrieving breakdown', commit)
            }
        }
    },
    async getAllAllocations({ commit, rootState }, { start, end }) {
        const params = {
            account_id: rootState.account.accountId,
            start: toStartISO(start),
            end: toEndISO(end)
        }

        try {
            const res = await webRequestClient.get('/audience_admin/activity/frontend/graph/audience/json', { params })
            if (res.status !== 'error' && res.message !== 'no matching \'ids\' found') {
                commit('setAllocationData', { data: res.data.ids[0] })
            }
            commit('setAllocationDataTimeUnit', { timeUnit: res.query.time_unit })
        } catch (error) {
            errorHandler(error, commit)
        }
    },
    async getKibanaData({ commit, rootState }, { start, end }) {
        const params = {
            account_id: rootState.account.accountId,
            end: toEndISO(end),
            start: toStartISO(start)
        }

        try {
            const res = await webRequestClient.get('/audience_admin/activity/kibana/frontend/graph/json', { params })
            commit('setKibanaData', { data: res.data.ids[0] })
        } catch (error) {
            errorHandler(error, commit)
        }
    },
    async getCollectOffline({ commit, rootState}, { start_date, end_date}) {
        commit('setCollectOfflineLoading', true)
        const params = {
            account_id: rootState.account.accountId,
            start_date,
            end_date
        }

        try {
            const res = await webRequestClient.get('/audience_admin/feedcrm_jobs/by_status/json', { params })
            commit('setCollectOffline', mapCollectOffline(res))
            commit('setCollectOfflineLoading', false)

        } catch (error) {
            commit('setCollectOfflineLoading', false)
            errorHandler(error, commit)
        }
    },
    async getCollectOnline({ commit, rootState }, { start_date, end_date}) {
        commit('setCollectOfflineLoading', true)
        const params = {
            account_id: rootState.account.accountId,
            start: toStartISOFormatted(start_date),
            end: toEndISOFormatted(end_date)
        }

        try {
            const res = await webRequestClient.get('/audience_admin/activity/frontend/graph/collect/json', { params })
            const mappedData = mapCollectOnline(res, isLessThan3Days(params.start, params.end))
            commit('setCollectOnline', mappedData)
            commit('setCollectOnlineLoading', false)

        } catch (error) {
            commit('setCollectOfflineLoading', false)
            errorHandler(error, commit)
        }
    },
    async getFormulaMetrics({ commit, dispatch, rootState, state }, { type, formula, tryCounter = 0 }) {
        const time = Date.now()
        const loadingKey = 'loadingVolumesToken'
        const loadingMutationName = 'setLoadingVolumesToken'
        commit(loadingMutationName, time)
        const params = {
            account_id: rootState.account.accountId,
            formula: formula.v2,
            method: 'count',
            version: 2,
        }

        try {
            commit('setLoadingVolumes', true)
            const res = await webRequestClient.post('/audience_admin/criteria/statistic.json', params)

            if (res.status === 'NOT_READY') {
                if (tryCounter < 30) {
                    setTimeout(() => {
                        dispatch('getFormulaMetrics', { type, formula, tryCounter: tryCounter + 1 })
                    }, 1000)
                } else {
                    if (state[loadingKey] == time) {
                        commit('setLoadingVolumes', false)
                        commit('setVolumesError', false)
                        errorHandler(`Unable to retrieve ${type}`, commit)
                    }
                }
            } else {
                if (state[loadingKey] == time) {
                    commit('setVolumes', res)
                    commit('setLoadingVolumes', false)
                    commit('setVolumesError', false)
                }
            }
        } catch(_err) {
            if (state[loadingKey] == time) {
                commit('setLoadingVolumes', false)
                commit('setVolumesError', true)
                errorHandler(`Unable to retrieve ${type}`, commit)
            }
        }
    },

    async getTaxonomiesBreakdown({ commit, dispatch, rootState, state  }, { taxonomyId, tryCounter = 0 } = {}) {
        if (!taxonomyId) return
        const time = Date.now()
        const loadingKey = 'loadingTaxonomyBreakdownToken'
        const loadingMutationName = 'setLoadingTaxonomyBreakdownToken'
        commit(loadingMutationName, time)

        try {
            const params = {
                format: 'json',
                account_id: rootState.account.accountId,
                taxonomy_id: taxonomyId
            }
            commit('setTaxonomiesBreakdownAffinity', [])
            commit('setTaxonomiesBreakdownPurcent', [])
            commit('setTaxonomiesBreakdownLoading', true)
            const res = await webRequestClient.post('/audience_admin/criteria/statistic/taxonomy/breakdown/json', params)
            if (res.status === 'NOT_READY') {
                if (tryCounter < 30) {
                    setTimeout(() => {
                        dispatch('getTaxonomiesBreakdown', { taxonomyId, tryCounter: tryCounter + 1 })
                    }, 1000)
                } else if (state[loadingKey] == time) {
                    errorHandler(`Unable to retrieve the taxonomy: ${taxonomyId}`, commit)
                    commit('setTaxonomiesBreakdownLoading', false)
                }
            } else if (state[loadingKey] == time) {
                const affinity = mapTaxonomiesBreakdown(res.clusters, 'affinity')
                const purcent = mapTaxonomiesBreakdown(res.clusters, 'cluster_in_segment')
                commit('setTaxonomiesBreakdownAffinity', affinity ?? [])
                commit('setTaxonomiesBreakdownPurcent', purcent ?? [])
                commit('setTaxonomiesBreakdownLoading', false)
            }
        } catch (_err) {
            if (state[loadingKey] == time) {
                const errorAuthorization = [
                    '"breakdown.v1: Access forbidden: this account isn\'t authorized to access to Breakdown"',
                    '"breakdown.v1: Access forbidden: this account isn\'t authorized to access to Breakdown Bigsea Sociodemo and Bigsea Behavioral"'
                ]
                if (!errorAuthorization.includes(_err.apiError?.message)) {
                    errorHandler(`Unable to retrieve ${taxonomyId}`, commit)
                }
                commit('setTaxonomiesBreakdownLoading', false)
            }
        }
    },
}
export default actions
